"use client";

import { getScopedStorageKey } from "@/lib/sync/storage-scope";

export type PendingSyncOperationSummary = {
  type: string;
  sessionId?: string;
  programDayId?: string;
  clientProgramId?: string;
  retryCount?: number;
  retryNotBeforeMs?: number;
  retryNotBeforeIso?: string;
  queuedAt?: string;
  lastError?: string;
  updatesCount?: number;
  exerciseLogsCount?: number;
};

export type PendingSyncDebugState = {
  pendingSync: PendingSyncOperationSummary[];
  pendingSyncCount: number;
  queueHead: PendingSyncOperationSummary | null;
  trackedSessionIds: string[];
};

function safeParseJson(value: string | null): unknown {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function summarizePendingSyncOperation(op: unknown): PendingSyncOperationSummary {
  if (!op || typeof op !== "object") return { type: "unknown" };
  const value = op as Record<string, unknown>;
  const data = value.data && typeof value.data === "object" ? (value.data as Record<string, unknown>) : {};
  return {
    type: typeof value.type === "string" ? value.type : "unknown",
    sessionId: typeof data.sessionId === "string" ? data.sessionId : undefined,
    programDayId: typeof data.programDayId === "string" ? data.programDayId : undefined,
    clientProgramId: typeof data.clientProgramId === "string" ? data.clientProgramId : undefined,
    retryCount: typeof value._retryCount === "number" ? value._retryCount : undefined,
    retryNotBeforeMs:
      typeof value._retryNotBeforeMs === "number" && Number.isFinite(value._retryNotBeforeMs)
        ? value._retryNotBeforeMs
        : undefined,
    retryNotBeforeIso:
      typeof value._retryNotBeforeMs === "number" && Number.isFinite(value._retryNotBeforeMs)
        ? new Date(value._retryNotBeforeMs).toISOString()
        : undefined,
    queuedAt: typeof value._queuedAt === "string" ? value._queuedAt : undefined,
    lastError: typeof value._lastError === "string" ? value._lastError : undefined,
    updatesCount: Array.isArray(data.updates) ? data.updates.length : undefined,
    exerciseLogsCount: Array.isArray(data.exerciseLogs) ? data.exerciseLogs.length : undefined,
  };
}

export function summarizePendingSyncOperations(raw: unknown): PendingSyncOperationSummary[] {
  if (!Array.isArray(raw)) return [];
  return raw.slice(-20).map((op) => summarizePendingSyncOperation(op));
}

export function buildPendingSyncDebugState(raw: unknown): PendingSyncDebugState {
  const pendingSync = summarizePendingSyncOperations(raw);
  const trackedSessionIds = Array.from(
    new Set(
      pendingSync
        .map((operation) => operation.sessionId)
        .filter((sessionId): sessionId is string => typeof sessionId === "string" && sessionId.length > 0)
    )
  );

  return {
    pendingSync,
    pendingSyncCount: pendingSync.length,
    queueHead: pendingSync[0] ?? null,
    trackedSessionIds,
  };
}

export function readPendingSyncDebugState(): PendingSyncDebugState {
  if (typeof window === "undefined") {
    return {
      pendingSync: [],
      pendingSyncCount: 0,
      queueHead: null,
      trackedSessionIds: [],
    };
  }

  const pendingSyncRaw = safeParseJson(localStorage.getItem(getScopedStorageKey("pendingSyncOperations")));
  return buildPendingSyncDebugState(pendingSyncRaw);
}
